import React, { useEffect, useState } from "react";
import { Typography, Tooltip, Tag, Skeleton, Timeline } from "antd";
import { AppleOutlined, AndroidOutlined, LaptopOutlined, DownOutlined, UpOutlined, BellFilled, MobileFilled } from "@ant-design/icons";
import { swimminglyApi } from "./utils";
import dayjs from "dayjs";
import DOMPurify from "dompurify";

const { Title } = Typography;

interface ReleaseNote {
  releaseId: number;
  releaseDate: string;
  appUpdated: string;
  appsUpdated?: string[] | null;
  releaseVersion: string;
  releaseDescription: string;
  deletedAt: string | null;
}

interface MostRecentReleases {
  clubhouse: ReleaseNote | null;
  ios: ReleaseNote | null;
  fanIos: ReleaseNote | null;
  android: ReleaseNote | null;
  fanAndroid: ReleaseNote | null;
}

const getAppIcon = (appUpdated: string) => {
  switch (appUpdated.toLowerCase()) {
    case "clubhouse":
      return <LaptopOutlined style={{ fontSize: '18px', color: 'magenta' }} />;
    case "swimminglyios":
      return <AppleOutlined style={{ fontSize: '18px', color: 'blue' }} />;
    case "swimminglyfanios":
      return <AppleOutlined style={{ fontSize: '18px', color: 'blue' }} />;
    case "swimminglyandroid":
      return <AndroidOutlined style={{ fontSize: '18px', color: 'green' }} />;
    case "swimminglyfanandroid":
      return <AndroidOutlined style={{ fontSize: '18px', color: 'green' }} />;
    default:
      return null;
  }
};

const getAppUserFacingName = (appUpdated: string) => {
  switch (appUpdated.toLowerCase()) {
    case "clubhouse":
      return "Clubhouse";
    case "swimminglyios":
      return "Swimmingly";
    case "swimminglyfanios":
      return "SwimminglyFan";
    case "swimminglyandroid":
      return "Swimmingly Timer";
    case "swimminglyfanandroid":
      return "SwimminglyFan";
    default:
      return appUpdated;
  }
};

/** Returns the canonical list of apps a note applies to, falling back to [appUpdated] for old rows. */
const getNoteApps = (note: ReleaseNote): string[] =>
  Array.isArray(note.appsUpdated) && note.appsUpdated.length > 0
    ? note.appsUpdated
    : [note.appUpdated];

/** Ordered list of accordion sections with their canonical app key. */
const APP_BUCKETS: { key: keyof MostRecentReleases; appKey: string }[] = [
  { key: "clubhouse", appKey: "clubhouse" },
  { key: "ios", appKey: "swimminglyIos" },
  { key: "fanIos", appKey: "swimminglyfanIos" },
  { key: "android", appKey: "swimminglyAndroid" },
  { key: "fanAndroid", appKey: "swimminglyfanAndroid" },
];

const UserReleaseNotes: React.FC = () => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [mostRecentReleases, setMostRecentReleases] = useState<MostRecentReleases>({
    clubhouse: null,
    ios: null,
    fanIos: null,
    android: null,
    fanAndroid: null,
  });
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState<string | null>(null); // Track which app is expanded

  // Fetch release notes from the backend
  const fetchReleaseNotes = async () => {
    setLoading(true);
    try {
      const response = await swimminglyApi.get("/api/releaseNotes");
      const validReleaseNotes = response.releaseNotes.filter((note: ReleaseNote) => !note.deletedAt);
      setReleaseNotes(validReleaseNotes);

      const recentReleases = getMostRecentReleases(validReleaseNotes);
      setMostRecentReleases(recentReleases);
    } catch (error) {
      console.error("Failed to fetch release notes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to find the most recent releases
  const getMostRecentReleases = (notes: ReleaseNote[]): MostRecentReleases => {
    const apps: MostRecentReleases = {
      clubhouse: null,
      ios: null,
      fanIos: null,
      android: null,
      fanAndroid: null,
    };

    notes.forEach((note) => {
      const noteApps = getNoteApps(note).map((a) => a.toLowerCase());
      if (noteApps.includes("clubhouse") && isNewerVersion(apps.clubhouse, note)) {
        apps.clubhouse = note;
      }
      if (noteApps.includes("swimminglyios") && isNewerVersion(apps.ios, note)) {
        apps.ios = note;
      }
      if (noteApps.includes("swimminglyfanios") && isNewerVersion(apps.fanIos, note)) {
        apps.fanIos = note;
      }
      if (noteApps.includes("swimminglyandroid") && isNewerVersion(apps.android, note)) {
        apps.android = note;
      }
      if (noteApps.includes("swimminglyfanandroid") && isNewerVersion(apps.fanAndroid, note)) {
        apps.fanAndroid = note;
      }
    });

    return apps;
  };

  // Helper function to check for newer version
  const isNewerVersion = (current: ReleaseNote | null, candidate: ReleaseNote): boolean => {
    if (!current) return true;
    return current.releaseVersion.localeCompare(candidate.releaseVersion, undefined, { numeric: true }) < 0;
  };

  useEffect(() => {
    fetchReleaseNotes();
  }, []);

  const formatReleaseDate = (releaseDate: string) => {
     return dayjs(releaseDate).format("MM-DD-YYYY");
  };

  const handleToggle = (appUpdated: string) => {
    setExpandedApp((prev) => (prev === appUpdated ? null : appUpdated)); // Toggle expansion
  };

  return (
    <div style={{ width: "100%", maxWidth: "440px" }}>
      {loading ? (
        <Skeleton active />
      ) : (
        <>
         <Title level={3}>
            <Tooltip title="Go to device compatibility" placement="right">
              <a href="https://support.swimmingly.app/device-compatibility" target="_blank" rel="noopener noreferrer">
                Current App Versions
              </a>
            </Tooltip>
            <Tooltip title="Go to Device Compatibility">
                      <MobileFilled
                        onClick={() => window.open("https://support.swimmingly.app/device-compatibility", "_blank")}
                        style={{ fontSize: '18px', color: '#1890ff', marginLeft: '10px', cursor: 'pointer' }}
                      />
                    </Tooltip>
                    {/* Add BellFilled icon with Tooltip */}
                    <Tooltip title="Go to Update Announcements">
                      <BellFilled
                        onClick={() => window.open("https://swimmingly.app/updates", "_blank")}
                        style={{ fontSize: '18px', color: '#1890ff', marginLeft: '10px', cursor: 'pointer' }}
                      />
                    </Tooltip>
          </Title>

          {APP_BUCKETS
            .filter(({ key }) => mostRecentReleases[key] !== null)
            .map(({ key, appKey }) => {
              const release = mostRecentReleases[key] as ReleaseNote;
              const icon = getAppIcon(appKey);
              const iconColor = icon?.props?.style?.color as string | undefined;
              return (
                <div key={appKey} style={{ marginBottom: '20px' }}>
                  <div
                    onClick={() => handleToggle(appKey)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 5px',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                      {icon}
                      <Tag color={iconColor} style={{ marginLeft: '10px', fontSize: '18px' }}>
                        {getAppUserFacingName(appKey)}
                      </Tag>
                      <Tag color={iconColor} style={{ marginLeft: 'auto', fontSize: '18px' }}>
                        {release.releaseVersion}
                      </Tag>
                    </div>
                    {expandedApp === appKey ? <UpOutlined /> : <DownOutlined />}
                  </div>
                  {expandedApp === appKey && (
                    <div style={{ marginTop: 8 }}>
                      <Typography.Title level={4} style={{ marginBottom: 8, color: "#101242" }}>
                        Release Notes
                      </Typography.Title>
                      <Timeline style={{ paddingLeft: 4 }}>
                        {releaseNotes
                          .filter((note) =>
                            getNoteApps(note).some((a) => a.toLowerCase() === appKey.toLowerCase())
                          )
                          .map((item) => (
                            <Timeline.Item
                              key={item.releaseId}
                              dot={icon}
                            >
                              <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                                <Tag
                                  color={iconColor}
                                  style={{ fontSize: "14px", marginRight: 8 }}
                                >
                                  {item.releaseVersion}
                                </Tag>
                                <span style={{ color: "#888", fontSize: "14px" }}>
                                  {formatReleaseDate(item.releaseDate)}
                                </span>
                              </div>
                              <div
                                style={{ marginTop: 4 }}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(item.releaseDescription || ""),
                                }}
                              />
                            </Timeline.Item>
                          ))}
                      </Timeline>
                    </div>
                  )}
                </div>
              );
            })}
        </>
      )}
    </div>
  );
};

export default UserReleaseNotes;
